import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { fileUrl, eur } from "../lib/api";

const CartDrawer = () => {
  const { isOpen, setIsOpen, items, setQty, removeItem, subtotal, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [pathname, setIsOpen]);

  const goToCheckout = () => {
    setIsOpen(false);
    if (!user) {
      toast.error("Please sign in to place your order");
      navigate("/auth?next=/checkout");
      return;
    }
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-[#0A0A0A] border-l border-white/10 z-[70] flex flex-col"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
              <p className="text-xs tracking-[0.35em] uppercase text-gold">Your Selection</p>
              <button
                data-testid="cart-close-button"
                onClick={() => setIsOpen(false)}
                className="text-zinc-400 hover:text-gold transition-colors"
                aria-label="Close cart"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <p className="font-serif text-2xl text-zinc-400 italic">Your selection awaits</p>
                  <p className="mt-3 text-sm text-zinc-600">Add a piece to begin your conquest.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-8">
                  {items.map((item) => (
                    <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-5">
                      <div className="w-20 h-24 bg-[#141414] border border-white/10 overflow-hidden flex-shrink-0">
                        {item.image && (
                          <img src={fileUrl(item.image)} alt={item.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 truncate">{item.partner}</p>
                        <p className="font-serif text-lg text-[#F5F5F5] leading-tight mt-1">{item.name}</p>
                        <p className="text-xs text-zinc-500 mt-1">Size {item.size}</p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/10">
                            <button
                              data-testid={`cart-qty-minus-${item.key}`}
                              onClick={() => setQty(item.key, item.qty - 1)}
                              className="p-1.5 text-zinc-400 hover:text-gold transition-colors"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="px-3 text-xs text-zinc-300">{item.qty}</span>
                            <button
                              data-testid={`cart-qty-plus-${item.key}`}
                              onClick={() => setQty(item.key, item.qty + 1)}
                              className="p-1.5 text-zinc-400 hover:text-gold transition-colors"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-zinc-600">{eur(item.price)} &times; {item.qty}</p>
                            <p className="text-sm text-gold" data-testid={`cart-line-total-${item.key}`}>{eur(item.price * item.qty)}</p>
                          </div>
                        </div>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.key}`}
                        onClick={() => removeItem(item.key)}
                        className="self-start text-zinc-600 hover:text-red-400 transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-8 py-6">
                <div className="flex flex-col gap-2 mb-6 text-xs">
                  <div className="flex justify-between text-zinc-500">
                    <span className="tracking-[0.25em] uppercase">Pieces</span>
                    <span data-testid="cart-count">{count}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span className="tracking-[0.25em] uppercase">Subtotal</span>
                    <span data-testid="cart-subtotal">{eur(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500">
                    <span className="tracking-[0.25em] uppercase">Shipping</span>
                    <span>Complimentary</span>
                  </div>
                  <div className="h-px bg-gradient-to-r from-maroon-light/60 via-gold/40 to-transparent my-2" />
                  <div className="flex justify-between items-baseline">
                    <span className="tracking-[0.3em] uppercase text-zinc-300">Total</span>
                    <span data-testid="cart-total" className="font-serif text-3xl text-gold">{eur(subtotal)}</span>
                  </div>
                </div>
                <button
                  data-testid="cart-buy-all-button"
                  onClick={goToCheckout}
                  className="lux-shimmer w-full py-4 bg-maroon text-[#F5F5F5] text-xs tracking-[0.35em] uppercase font-semibold hover:bg-gold hover:text-black transition-colors duration-500"
                >
                  Buy All &middot; {eur(subtotal)}
                </button>
                <button
                  data-testid="cart-checkout-button"
                  onClick={goToCheckout}
                  className="mt-3 w-full py-3 border border-white/15 text-zinc-400 text-[11px] tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors duration-500"
                >
                  Proceed to Checkout
                </button>
                {!user && (
                  <p data-testid="cart-signin-notice" className="mt-4 text-center text-[10px] tracking-[0.25em] uppercase text-zinc-600">
                    Sign in required to place your order
                  </p>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
